import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import type { TenantDefaultUserMappingDetail } from "../lib/customers.js";
import { DefaultCoverageModal } from "./DefaultCoverageModal.js";

/**
 * ADR-0075 — Default coverage card.
 *
 * Used when a watch row's account code doesn't match any customer (or the
 * customer hierarchy exhausts with no users at any level). The default is a
 * tenant-level set of role-typed mappings, NOT a customer row.
 *
 * Sits above the customers table on /app/admin/customers. Always visible —
 * shows configured mappings inline (or an empty-state nudge when unset) and
 * opens a modal for editing.
 */
export function DefaultCoverageCard() {
  const [mappings, setMappings] = useState<TenantDefaultUserMappingDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ mappings: TenantDefaultUserMappingDetail[] }>(
        "/api/tenant/admin/customers/default-coverage"
      );
      setMappings(res.mappings);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const primary = mappings.find((m) => m.is_primary);
  const byRole = new Map<string, TenantDefaultUserMappingDetail[]>();
  for (const m of mappings) {
    const arr = byRole.get(m.role_label) ?? [];
    arr.push(m);
    byRole.set(m.role_label, arr);
  }

  return (
    <>
      <div className="card" style={{ background: "var(--surface-soft)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Default coverage</div>
            <div className="help muted" style={{ marginBottom: 8 }}>
              Used when a row's account code doesn't match any customer below, or the customer's
              hierarchy walks to the top without finding a mapped user.
            </div>
            {loading && <div className="muted">Loading…</div>}
            {error && (
              <div className="help" style={{ color: "var(--danger-text)" }}>
                {error}
              </div>
            )}
            {!loading && !error && mappings.length === 0 && (
              <div className="muted">
                No mappings configured. Dynamic mapping will not fall back until you add at least a
                primary user.
              </div>
            )}
            {!loading && !error && mappings.length > 0 && (
              <div style={{ display: "grid", gap: 4 }}>
                {primary && (
                  <div>
                    <span className="label">Primary:</span>{" "}
                    {primary.display_name ?? primary.email}{" "}
                    <span className="muted">({primary.role_label})</span>
                  </div>
                )}
                {Array.from(byRole.entries()).map(([role, members]) => (
                  <div key={role}>
                    <span className="label">{role}:</span>{" "}
                    {members.map((m) => m.display_name ?? m.email).join(", ")}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <button className="btn" onClick={() => setOpen(true)}>
              Manage default coverage
            </button>
          </div>
        </div>
      </div>
      {open && (
        <DefaultCoverageModal
          onClose={() => {
            setOpen(false);
            void load();
          }}
        />
      )}
    </>
  );
}
